import type { Node, Edge } from '@xyflow/react';

/**
 * 车道吸附 v3 —— 边驱动虚拟参考线（lane snap via edge constraints）
 *
 * 根因链（为什么线会扭曲、为什么前两版吸附无效）：
 *  1. React Flow smoothstep 只要两端 handle 的 y 相差 1px 就会插入圆角拐点；
 *  2. 静态坐标对齐受字体渲染误差影响，中线偏差可达 5~7px；
 *  3. v1 的"距离聚类"阈值 4px 盖不住 5px+ 的偏差；
 *  4. v2 改用边驱动并查集（正确），但用 `rfInstance.setNodes()` 应用结果——
 *     **受控 nodes 模式下 store 改动会被 props 同步回滚**，吸附从未生效
 *     （症状：每次调用都报 changed=N，永不收敛为 0）。
 *
 * v3 方案：只做测量与计算，返回每个节点的**目标 y**（flow 坐标），
 * 由调用方写回自己的 nodes 数据源（state）——受控 prop 的正道，改动必定生效：
 *  - 凡是一条边两端把手都是水平方向（left/right）且中线差 ≤ TOL，判定"应为水平直线"，
 *    并查集合并两端节点；
 *  - 每组取 SVG 尺寸最大的设备为锚点，锚点中线即**虚拟参考线**，组内其余节点对齐过来；
 *  - 调用方在 120ms / fonts.ready 后各测一次 setState（字体加载会漂移 1~2px，二次收敛）。
 */

const TOL = 12; // 两端中线差 ≤ 此值判定为"应为直线"（车道间距 ≥ 40px，不会误并）

export interface SnapLanesRF {
  getNodes: () => Node[];
  getEdges: () => Edge[];
}

class DSU {
  private parent = new Map<string, string>();
  find(x: string): string {
    let r = this.parent.get(x) ?? x;
    if (r !== x && this.parent.has(x)) r = this.find(r);
    this.parent.set(x, r);
    return r;
  }
  union(a: string, b: string): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent.set(ra, rb);
  }
}

const isHorizontalHandle = (h?: string | null) =>
  !!h && (h.startsWith('left') || h.startsWith('right'));

/** 测量并计算各节点目标 y；返回需要移动的 { nodeId: 目标y }（flow 坐标），空对象表示已对齐 */
export function measureLaneTargets(rf: SnapLanesRF): Record<string, number> {
  const pane = document.querySelector('.react-flow')?.getBoundingClientRect();
  const vp = document.querySelector('.react-flow__viewport') as HTMLElement | null;
  if (!pane || !vp) return {};
  const m = /translate\(([-\d.]+)px,\s*([-\d.]+)px\)\s*scale\(([\d.]+)\)/.exec(vp.style.transform);
  if (!m) return {};
  const ty = parseFloat(m[2]);
  const s = parseFloat(m[3]);
  if (!s) return {};

  // 测量每个节点 div 的真实几何（flow 坐标）：中线 / 高度 / SVG 高度
  const midOf = new Map<string, number>();
  const heightOf = new Map<string, number>();
  const svgHOf = new Map<string, number>();
  for (const n of rf.getNodes()) {
    const el = document.querySelector(`.react-flow__node[data-id="${n.id}"]`);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.height === 0) continue;
    midOf.set(n.id, (r.top + r.height / 2 - pane.top - ty) / s);
    heightOf.set(n.id, r.height / s);
    const svg = el.querySelector('svg');
    svgHOf.set(n.id, svg ? svg.getBoundingClientRect().height / s : 0);
  }
  if (midOf.size === 0) return {};

  // 边驱动约束：两端均为水平把手且中线差 ≤ TOL → 同一组
  const dsu = new DSU();
  for (const e of rf.getEdges()) {
    if (!midOf.has(e.source) || !midOf.has(e.target)) continue;
    if (!isHorizontalHandle(e.sourceHandle) || !isHorizontalHandle(e.targetHandle)) continue;
    if (Math.abs(midOf.get(e.source)! - midOf.get(e.target)!) > TOL) continue;
    dsu.union(e.source, e.target);
  }

  // 分组；虚拟参考线 = 组内 SVG 最大设备的中线
  const groups = new Map<string, { anchorSvgH: number; anchorMid: number; ids: string[] }>();
  Array.from(midOf.keys()).forEach((id) => {
    const mid = midOf.get(id)!;
    const root = dsu.find(id);
    let g = groups.get(root);
    if (!g) {
      g = { anchorSvgH: svgHOf.get(id) ?? 0, anchorMid: mid, ids: [] };
      groups.set(root, g);
    }
    g.ids.push(id);
    const h = svgHOf.get(id) ?? 0;
    if (h > g.anchorSvgH) {
      g.anchorSvgH = h;
      g.anchorMid = mid;
    }
  });

  // 输出目标 y = 参考线 - div 高/2（锚点 delta≈0 自然不输出）
  const targets: Record<string, number> = {};
  Array.from(groups.values()).forEach((g) => {
    g.ids.forEach((id) => {
      const targetY = g.anchorMid - heightOf.get(id)! / 2;
      const curY = midOf.get(id)! - heightOf.get(id)! / 2;
      if (Math.abs(targetY - curY) < 0.01) return;
      targets[id] = targetY;
    });
  });
  return targets;
}

